/**
 * Account Status Axios Interceptor (Feb 2026)
 *
 * Backend account_status_middleware'inden gelen 401 + code:
 *   - 'account_inactive'  → "Hesabınız pasifleştirilmiştir. Firmanız (X) ile görüşün"
 *   - 'session_revoked'   → "Oturumunuz yöneticiniz tarafından sonlandırıldı"
 *   - 'user_not_found'    → "Hesabınız sistemde bulunamadı"
 *
 * Yakalandığında:
 *   1. toast ile mesaj göster
 *   2. localStorage/sessionStorage temizle
 *   3. /login'e yönlendir
 *
 * Tek seferlik tetiklenir (duplicate önleme guard'lı).
 */
import axios from 'axios';
import { toast } from 'sonner';
import { olayBildir } from './olayBildir';

let _logoutInProgress = false;
const LOGOUT_DELAY_MS = 2500;

function triggerForceLogout(code, message) {
  if (_logoutInProgress) return;
  _logoutInProgress = true;
  olayBildir('zorunlu_cikis', { sebep: code || 'bilinmiyor', mesaj: String(message || '').slice(0, 200) });

  try {
    toast.error(message || 'Oturumunuz sonlandırıldı', { duration: LOGOUT_DELAY_MS });
  } catch (_) { /* ignore */ }

  setTimeout(() => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.clear();
    } catch (_) { /* ignore */ }
    // Hard redirect — tüm state'i temizle
    const reason = encodeURIComponent(code || 'logout');
    window.location.replace(`/login?reason=${reason}`);
  }, LOGOUT_DELAY_MS);
}

export function installAccountStatusInterceptor() {
  // İstek süresini ölç — zaman aşımı mı, anında bağlantı hatası mı ayırt edilsin
  axios.interceptors.request.use((config) => {
    try { config.metadata = { ...(config.metadata || {}), t0: Date.now() }; } catch (_) { /* atla */ }
    return config;
  });
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      try {
        const status = error?.response?.status;
        const body = error?.response?.data;
        // Sunucu hatası / yanıt yok → Sistem Sağlığı'nda görünsün (dakikada sınırlı).
        // 2026-10-12: zaman aşımı ve iptal de "sunucuya ulaşılamadı" sayılıyordu → her 5 dk'da 8 sn'yi aşan tek
        // bir uç (fırsat şeridi) yanlış "kullanıcılar sunucuya ulaşamıyor" alarmı üretiyordu. Ayrıldı:
        //   iptal → bildirilmez · zaman aşımı → istek_zaman_asimi · yanıt yok → sunucuya_ulasilamadi
        const iptal = axios.isCancel?.(error) || error?.code === 'ERR_CANCELED';
        if (!iptal && (!status || status >= 500)) {
          const cfg = error?.config || {};
          const url = String(cfg.url || '').replace(/^https?:\/\/[^/]+/, '').split('?')[0];
          if (!url.includes('/sistem/istemci-olay')) {
            const zamanAsimi = error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT'
              || /timeout/i.test(String(error?.message || ''));
            const tur = status ? 'sunucu_hatasi' : (zamanAsimi ? 'istek_zaman_asimi' : 'sunucuya_ulasilamadi');
            olayBildir(tur, {
              durum: status || null, url, kod: error?.code || '', yontem: cfg.method || '',
              sure_ms: cfg.metadata?.t0 ? Date.now() - cfg.metadata.t0 : null,
              zaman_asimi_ms: cfg.timeout || null,
              cevrimici: typeof navigator !== 'undefined' ? navigator.onLine : null,
              sekme_gorunur: typeof document !== 'undefined' ? document.visibilityState === 'visible' : null,
            });
          }
        }
        if (status === 401 && body && typeof body === 'object') {
          const code = body.code;
          const msg = body.error || body.detail || 'Oturumunuz sonlandı';
          if (code === 'account_inactive' || code === 'session_revoked' || code === 'user_not_found' || code === 'mesai_disi') {
            triggerForceLogout(code, msg);
          }
        }
      } catch (_) { /* atla */ }
      return Promise.reject(error);
    }
  );
}
